export type UserRole = 'super_user' | 'site_admin' | 'end_user';

export interface NavigationItem {
  path: string;
  label: string;
  roles: UserRole[];
}

export interface Site {
  id: string;
  name: string;
  address?: string;
  description?: string;
}

export interface Area {
  id: string;
  name: string;
  siteControllerId: string;
  presetId?: string | null;
}

export interface Group {
  id: string;
  name: string;
  siteId: string;
}

export interface Preset {
  id: string;
  name: string;
  areaId: string;
  groupIds?: string[];
}

/**
 * Check if a user can access a route based on their role
 */
export function canAccessRoute(userRole: UserRole | undefined, route: string): boolean {
  if (!userRole) return false;

  const routePermissions: Record<string, UserRole[]> = {
    '/': ['super_user', 'site_admin', 'end_user'],
    '/sites': ['super_user', 'site_admin', 'end_user'],
    '/users': ['super_user', 'site_admin'],
    '/groups': ['super_user', 'site_admin'],
    '/audit': ['super_user'],
    '/logs': ['super_user'],
    '/branding': ['super_user'],
    '/organisation': ['super_user'],
  };

  const allowedRoles = routePermissions[route];
  return allowedRoles ? allowedRoles.includes(userRole) : false;
}

/**
 * Check if a user can manage sites
 */
export function canManageSites(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user';
}

/**
 * Check if a user can manage users
 */
export function canManageUsers(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user' || userRole === 'site_admin';
}

/**
 * Check if a user can manage groups
 */
export function canManageGroups(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user' || userRole === 'site_admin';
}

/**
 * Check if a user can view audit logs
 */
export function canViewAuditLogs(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user';
}

/**
 * Check if a user can configure site components (controllers, areas, presets)
 */
export function canConfigureSiteComponents(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user' || userRole === 'site_admin';
}

/**
 * Check if a user can edit light preset group aliases (dashboard display names).
 * Site Admins may view aliases but only Super Users may change them.
 */
export function canEditAlias(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user';
}

/**
 * Check if a user can control areas (set presets)
 */
export function canControlAreas(): boolean {
  return true; // All authenticated users can control areas (with group restrictions)
}

/**
 * Filter sites based on user role and assigned sites
 * Note: Backend already filters, but this can be used for client-side filtering if needed
 */
export function filterSitesByRole(
  sites: Site[],
  userRole: UserRole | undefined,
  userSiteIds?: string[]
): Site[] {
  if (!userRole) return [];

  if (userRole === 'super_user') {
    return sites; // Super users see all sites
  }

  // Site admins and end users only see their assigned sites
  if (userSiteIds && userSiteIds.length > 0) {
    return sites.filter((site) => userSiteIds.includes(site.id));
  }

  return [];
}

/**
 * Get areas accessible to an end user via their groups
 * This is a helper for client-side filtering (backend also enforces this)
 */
export function getAccessibleAreas(
  areas: Area[],
  presets: Preset[],
  userGroupIds: string[]
): Area[] {
  if (userGroupIds.length === 0) {
    return []; // No groups = no access
  }

  // Get preset IDs that the user has access to via their groups
  const accessiblePresetIds = new Set<string>();
  presets.forEach((preset) => {
    if (preset.groupIds && preset.groupIds.some((groupId) => userGroupIds.includes(groupId))) {
      accessiblePresetIds.add(preset.id);
    }
  });

  // Return areas that have accessible presets
  return areas.filter((area) => {
    // If area has a preset, check if user has access to it
    if (area.presetId) {
      return accessiblePresetIds.has(area.presetId);
    }
    // If area has no preset, check if any accessible preset belongs to this area
    return presets.some(
      (preset) => preset.areaId === area.id && accessiblePresetIds.has(preset.id)
    );
  });
}

/**
 * Get presets accessible to an end user via their groups
 */
export function getAccessiblePresets(presets: Preset[], userGroupIds: string[]): Preset[] {
  if (userGroupIds.length === 0) {
    return [];
  }

  return presets.filter(
    (preset) => preset.groupIds && preset.groupIds.some((groupId) => userGroupIds.includes(groupId))
  );
}

/**
 * Check if a user can edit a site
 */
export function canEditSite(userRole: UserRole | undefined, siteId: string, userSiteIds?: string[]): boolean {
  if (userRole === 'super_user') {
    return true;
  }

  if (userRole === 'site_admin') {
    return userSiteIds ? userSiteIds.includes(siteId) : false;
  }

  return false;
}

/**
 * Check if a user can delete a site
 */
export function canDeleteSite(userRole: UserRole | undefined): boolean {
  return userRole === 'super_user';
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole | undefined): string {
  if (!role) return 'Unknown';

  const roleNames: Record<UserRole, string> = {
    super_user: 'Super User',
    site_admin: 'Site Administrator',
    end_user: 'End User',
  };

  return roleNames[role] || role;
}

/**
 * Get role badge variant for UI components
 */
export function getRoleBadgeVariant(role: UserRole | undefined): 'default' | 'secondary' | 'outline' {
  if (!role) return 'outline';

  const variants: Record<UserRole, 'default' | 'secondary' | 'outline'> = {
    super_user: 'default',
    site_admin: 'secondary',
    end_user: 'outline',
  };

  return variants[role] || 'outline';
}
